import {
  LoginUser,
  RoleType,
  PlanType,
  FeeType,
  PayType,
  Project,
  Client,
  Trial,
  Plan,
  RoleRequest
} from "../type/login-user";

export class LoginUserProto {
  private readonly loginUser: LoginUser;

  constructor(loginUser: LoginUser) {
    this.loginUser = loginUser;
  }

  // EXECUTE
  public get execute(): LoginUser {
    return this.loginUser;
  }

  // USER
  public get fallback(): string | null {
    const name = this.loginUser.name?.trim();
    if (!name) return null;
    const initials = name
      .split(/\s|-/)
      .filter((part) => part.length > 0)
      .map((part) => part[0].toUpperCase())
      .join("")
      .slice(0, 2);
    return initials || null;
  }

  public get name(): string | null {
    return this.loginUser.name;
  }

  public get email(): string | null {
    return this.loginUser.email;
  }

  public get isSupportAccount(): boolean {
    return this.email?.split("@").pop() === "veldt.jp";
  }

  public get role(): RoleType {
    switch (this.loginUser.role) {
      case 0:
        return "member";
      case 1:
        return "admin";
      case 2:
        return "partner";
      case 3:
        return "user";
      default:
        return "member";
    }
  }

  public hasRole(roles: RoleType[]): boolean {
    return roles.includes(this.role);
  }

  public hasRoleAccess(user: { email?: string }): boolean {
    return this.hasRole(["admin"]) || this.email === user.email;
  }

  public isSelfEditable(user: { email?: string }): boolean {
    return this.email === user.email;
  }

  public isExpert(): boolean {
    return this.hasRole(["admin", "member", "partner"])
  }

  // CLIENT
  public get client(): Client {
    return this.loginUser.client;
  }

  public get clientId(): string | null {
    return this.loginUser.client.id ?? null;
  }

  public get clientName(): string | null {
    return this.loginUser.client.name ?? null;
  }

  public get clientLogo(): string | null {
    return this.loginUser.client.logo ?? null;
  }

  // PLAN
  public get plan(): Plan {
    return this.loginUser.plan;
  }

  public get planId(): number | null {
    return this.loginUser.plan.plan_id;
  }

  public get planType(): PlanType | null {
    return this.loginUser.plan.plan_type;
  }

  public get feeType(): FeeType {
    return this.loginUser.plan.fee_type;
  }

  public get payType(): PayType {
    return this.loginUser.plan.pay_type;
  }

  public get isClientPlanActive(): boolean {
    return Boolean(this.loginUser.plan.is_plan_active);
  }

  public get isMobileLink(): boolean {
    return Boolean(this.loginUser.plan.is_mobile_link);
  }

  public get isMultiProject(): boolean {
    return Boolean(this.loginUser.plan.is_multi_project);
  }

  public get limitSize(): number | null {
    return this.loginUser.plan.limit_size ?? null;
  }

  public get nodeSize(): number | null {
    return this.loginUser.plan.node_size ?? null;
  }

  public get enableHealthcare(): boolean {
    return Boolean(
      this.loginUser.plan.plan_type === "healthcare" && this.isMobileLink
    );
  }

  public get enableStandard(): boolean {
    return Boolean(this.loginUser.plan.plan_type === "standard");
  }

  // PROJECT
  public get projects(): Project[] | [] {
    return this.loginUser.projects;
  }

  public get currentProject(): Project | null {
    return this.loginUser.projects.find((p) => p.id === this.clientId) ?? null;
  }

  // TRIAL
  public get trial(): Trial {
    return this.loginUser.trial;
  }

  // USED CAPACITY
  public get usedSize(): number | null {
    return this.loginUser.client.used_size;
  }

  // USED CODE
  public get usedCode(): number | null {
    return this.loginUser.client.used_code;
  }

  // ROLE REQUEST LIST
  public get roleRequests(): RoleRequest[] {
    return this.loginUser.client.role_requests;
  }

  public hasRoleRequest(): boolean {
    return (this.loginUser.client.role_requests.length > 0);
  }

  public get isWaitingReview(): boolean {
    return this.loginUser.client.is_waiting_review;
  }

  public get isApprovedReview(): boolean {
    return this.loginUser.client.is_approved_review;
  }

  public get isUseAssistant(): boolean {
    return this.loginUser.client.is_use_assistant;
  }

  /** true = 2 フェーズ取込 (4 段ダイアログ + v3 API)。false = 従来の 2 段 (legacy)。 */
  public get isNewDb(): boolean {
    return Boolean(this.loginUser.client.is_new_db);
  }

  public get platformUrl(): string | null {
    return this.loginUser.client.platform_url ?? null;
  }
}
